import { FC, useState } from 'react'

import { BigNumber } from 'ethers'

import Tile from '~/components/Tile'
import { PageHeader } from '~/components/PageHeader'
import StakeSelector from '~/components/StakeSelector'
import StakedInfo from '~/components/StakedInfo'
import { useEarn } from '~/lib/useEarns'
import { toAmount, toBigNumber } from '~/lib/helpers'
import { earnTypeByPathName, pathToEarnPage } from '~/lib/routes'
import { useNavigate } from 'react-router-dom'
import { useMatch } from 'react-router'
import Navigation from '~/components/Navigation'
import { EarnStatus } from '~/contracts/useEarnContract'
import { CONFIG } from '~/lib/config'
import { isCorrectDecimals } from '~/lib/isCorrectDecimals'
import { SwitchNetworkButton } from '~/components/SwitchNetworkButton'

const UnstakePage: FC = () => {
	const { contract } = useEarn()
	const [pending, setPending] = useState(false)

	const {
		params: { earnTypePath },
	} = useMatch('/lp-farm/:earnTypePath/:page')
	const earnType = earnTypeByPathName(earnTypePath)
	const navigate = useNavigate()

	const [rawAmount, setRawAmount] = useState<string>('')

	const isSuccess = contract?.status === EarnStatus.SUCCESS

	let balance
	let amount: BigNumber | undefined

	if (isSuccess) {
		const amountValue = Number.parseFloat(rawAmount)

		if (
			!Number.isNaN(amountValue) &&
			isCorrectDecimals(rawAmount, contract.token.decimals)
		) {
			amount = toBigNumber(contract.token.decimals, rawAmount)
		}

		balance = toAmount(
			contract.token.decimals,
			contract.earn.userInfo.amount
		)
	}

	const onSubmit = () => {
		setPending(true)
		contract
			.withdraw(amount)
			.catch(console.error)
			.finally(() => {
				contract.refresh()
				setPending(false)
				setRawAmount('')
			})
	}

	let disabled
	let buttonText
	if (!isSuccess) {
		disabled = true
		buttonText = 'Loading...'
	} else if (pending) {
		disabled = true
		buttonText = 'Unstaking...'
	} else {
		disabled =
			!(
				amount &&
				amount.gt(0) &&
				amount.lte(contract.earn.userInfo.amount)
			) || !isCorrectDecimals(rawAmount, contract.token.decimals)
		buttonText = 'Unstake'
	}

	return (
		<Tile>
			<Tile.Header>
				<PageHeader title="Unstake your LP" />
				<Navigation />
			</Tile.Header>

			<Tile.Item disabled={pending}>
				<StakeSelector
					onAmountChange={setRawAmount}
					amount={rawAmount}
					onDexChange={(earnType) =>
						navigate(pathToEarnPage(earnType, 'unstake'))
					}
					earnType={earnType}
					maxAmount={balance}
				/>
			</Tile.Item>

			<Tile.Item>
				<StakedInfo />
			</Tile.Item>

			<Tile.Footer>
				<SwitchNetworkButton
					block
					color="black"
					onClick={onSubmit}
					disabled={disabled}
					requiredChainId={CONFIG.earns[earnType].chainId}
				>
					{buttonText}
				</SwitchNetworkButton>
			</Tile.Footer>
		</Tile>
	)
}

export default UnstakePage
